import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const GOLD = "#d4af37";
const GOLD_HI = "#F0D580";
const GOLD_LO = "#8F6A2C";
const BG = "#0b0b0b";

/* ------------------------------------------------------------------ */
/* SEO meta                                                            */
/* ------------------------------------------------------------------ */
function upsertMeta(attr, key, value) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}
function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/* ------------------------------------------------------------------ */
/* Small icon set — hand-tuned gold-gradient SVGs                      */
/* ------------------------------------------------------------------ */
function GoldGradient({ id }) {
  return (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#F5E6B3" />
        <stop offset="45%" stopColor={GOLD} />
        <stop offset="100%" stopColor={GOLD_LO} />
      </linearGradient>
    </defs>
  );
}

const IconDesign = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <GoldGradient id="ic-design" />
    <rect x="4" y="6" width="32" height="22" rx="2" stroke={`url(#ic-design)`} strokeWidth="1.5" />
    <path d="M4 12 L36 12" stroke={`url(#ic-design)`} strokeWidth="1.5" />
    <circle cx="7" cy="9" r="0.9" fill={GOLD} />
    <circle cx="10" cy="9" r="0.9" fill={GOLD} />
    <rect x="8" y="16" width="10" height="8" rx="1" fill="rgba(212,175,55,0.15)" stroke={`url(#ic-design)`} strokeWidth="1" />
    <path d="M22 17 L32 17 M22 21 L30 21 M22 25 L28 25" stroke={`url(#ic-design)`} strokeWidth="1.4" strokeLinecap="round" />
    <path d="M14 32 L26 32" stroke={`url(#ic-design)`} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconAutomation = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <GoldGradient id="ic-auto" />
    <circle cx="20" cy="20" r="12.5" stroke={`url(#ic-auto)`} strokeWidth="1.5" />
    <circle cx="20" cy="20" r="6.5" stroke={`url(#ic-auto)`} strokeWidth="1.5" fill="rgba(212,175,55,0.08)" />
    <circle cx="20" cy="20" r="2" fill={GOLD} />
    {[0, 60, 120, 180, 240, 300].map((deg, i) => (
      <line
        key={i}
        x1="20" y1="6" x2="20" y2="3"
        stroke={`url(#ic-auto)`}
        strokeWidth="1.5"
        strokeLinecap="round"
        transform={`rotate(${deg} 20 20)`}
      />
    ))}
    <path d="M12 34 L28 34" stroke={`url(#ic-auto)`} strokeWidth="1.2" strokeDasharray="2 2" strokeLinecap="round" />
  </svg>
);

const IconSeo = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <GoldGradient id="ic-seo" />
    <circle cx="17" cy="17" r="10" stroke={`url(#ic-seo)`} strokeWidth="1.6" fill="rgba(212,175,55,0.08)" />
    <path d="M24 24 L33 33" stroke={`url(#ic-seo)`} strokeWidth="2" strokeLinecap="round" />
    <path d="M12 20 L15 15 L18 18 L22 12" stroke={`url(#ic-seo)`} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <circle cx="22" cy="12" r="1.4" fill={GOLD} />
  </svg>
);

const IconCheck = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden style={{ flexShrink: 0 }}>
    <GoldGradient id={`ic-check-${size}`} />
    <circle cx="12" cy="12" r="10.5" stroke={`url(#ic-check-${size})`} strokeWidth="1.2" fill="rgba(212,175,55,0.06)" />
    <path d="M7.5 12.4 L10.6 15.4 L16.5 8.9" stroke={`url(#ic-check-${size})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

/* ------------------------------------------------------------------ */
/* Intersection reveal helper                                          */
/* ------------------------------------------------------------------ */
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setVisible(true); }),
      { threshold: 0.12, rootMargin: "0px 0px -80px 0px" }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return [ref, visible];
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
export default function PoweredByNariai() {
  const nav = useNavigate();
  const location = useLocation();

  // SEO — this page is a NariAI page hosted on the client's domain.
  // Mark it noindex so it doesn't compete with the client's own indexed pages.
  useEffect(() => {
    document.title = "Powered by NariAI — Web design, automation, and SEO for local businesses.";
    upsertMeta(
      "name",
      "description",
      "This website was designed, engineered, and optimized by NariAI. Learn how we build fast, high-ranking, AI-powered websites for local businesses in Texas and beyond."
    );
    upsertMeta("name", "robots", "noindex,follow");
    upsertLink("canonical", `${process.env.REACT_APP_SITE_URL || window.location.origin}/powered-by-nariai`);
    try { document.documentElement.setAttribute("lang", "en"); } catch { /* noop */ }
  }, []);

  // On mount we jump to the top so people arriving from the footer link start clean.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const [heroRef, heroSeen] = useReveal();
  const [servRef, servSeen] = useReveal();
  const [statsRef, statsSeen] = useReveal();
  const [howRef, howSeen] = useReveal();
  const [ctaRef, ctaSeen] = useReveal();

  const services = [
    {
      icon: <IconDesign />,
      title: "Custom Website Design",
      lead: "Fortune-500 caliber design for businesses that don't feel like brochureware.",
      bullets: [
        "Hand-built layouts — never templates",
        "Sub-200 ms server response times",
        "Mobile-first, Core Web Vitals green",
        "Enterprise-grade design system per brand",
      ],
    },
    {
      icon: <IconAutomation />,
      title: "AI Business Automation",
      lead: "Smart tools that answer customers, book jobs, and never sleep.",
      bullets: [
        "24/7 AI chat that knows your business",
        "Automated lead routing to your phone or CRM",
        "Instant quote & estimate calculators",
        "Follow-up sequences that recover missed leads",
      ],
    },
    {
      icon: <IconSeo />,
      title: "Search Engine Optimization",
      lead: "Built to rank on day one and keep climbing every quarter.",
      bullets: [
        "Deep local SEO with city + industry silos",
        "Full JSON-LD schema on every page",
        "Bilingual hreflang for Hispanic markets",
        "Google Analytics 4 + Search Console wired",
      ],
    },
  ];

  const stats = [
    { value: "266+", label: "SEO pages engineered for this client alone" },
    { value: "20", label: "Native Spanish pages with full hreflang" },
    { value: "<200 ms", label: "Median server response time" },
    { value: "100 %", label: "Core Web Vitals · Mobile-first" },
    { value: "6", label: "Structured-data schemas per page" },
    { value: "24/7", label: "AI chat + lead capture always on" },
  ];

  const how = [
    { n: "01", title: "Discover", body: "We sit with you, walk your operation, and learn where your revenue actually leaks." },
    { n: "02", title: "Design", body: "Every layout is drafted from scratch to match your brand — no themes, no templates." },
    { n: "03", title: "Engineer", body: "We hand-build the site, wire the automation, and ship it fast on a modern stack." },
    { n: "04", title: "Grow", body: "We stay on retainer — SEO, updates, monitoring, and continuous improvements." },
  ];

  return (
    <div style={{ background: BG, color: "#F5F5F0", minHeight: "100vh" }}>
      {/* ------------- Top strip: back to client + brand mark ---------- */}
      <div
        style={{
          borderBottom: "1px solid rgba(212,175,55,0.14)",
          background: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(8px)",
          position: "sticky",
          top: 0,
          zIndex: 40,
        }}
      >
        <div className="max-w-6xl mx-auto" style={{ padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <button
            type="button"
            onClick={() => nav(-1)}
            className="font-inter"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "rgba(245,245,240,0.7)",
              fontSize: 12,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              transition: "color 200ms ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = GOLD_HI; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(245,245,240,0.7)"; }}
            data-testid="pbn-back-link"
          >
            <span aria-hidden style={{ fontSize: 14 }}>←</span>
            Back to the site
          </button>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: 999,
                background: `linear-gradient(180deg,${GOLD_HI} 0%,${GOLD} 50%,${GOLD_LO} 100%)`,
                boxShadow: "0 0 12px rgba(212,175,55,0.6)",
              }}
            />
            <span
              className="font-cab"
              style={{ color: "#FFFFFF", fontWeight: 800, letterSpacing: "-0.005em", fontSize: 15 }}
            >
              NariAI
            </span>
          </div>
        </div>
      </div>

      {/* ------------------------ HERO ------------------------------- */}
      <section
        ref={heroRef}
        style={{
          position: "relative",
          overflow: "hidden",
          padding: "112px 24px 88px 24px",
          textAlign: "left",
        }}
      >
        {/* Gold radial glow */}
        <div
          aria-hidden
          style={{
            position: "absolute", inset: 0,
            background: "radial-gradient(ellipse at 30% 20%, rgba(212,175,55,0.14), transparent 55%), radial-gradient(ellipse at 80% 70%, rgba(212,175,55,0.08), transparent 55%)",
            pointerEvents: "none",
          }}
        />
        {/* Fine gold hairline top */}
        <div
          aria-hidden
          style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 1,
            background: "linear-gradient(90deg, transparent 0%, rgba(212,175,55,0.55) 50%, transparent 100%)",
          }}
        />
        <div
          className="max-w-6xl mx-auto"
          style={{
            position: "relative",
            transform: heroSeen ? "translateY(0)" : "translateY(24px)",
            opacity: heroSeen ? 1 : 0,
            transition: "opacity 900ms ease, transform 900ms cubic-bezier(.2,.8,.2,1)",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "6px 12px",
              borderRadius: 999,
              border: "1px solid rgba(212,175,55,0.35)",
              background: "rgba(212,175,55,0.06)",
              color: GOLD,
              fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
              fontSize: 10,
              letterSpacing: "0.28em",
              textTransform: "uppercase",
            }}
            data-testid="pbn-eyebrow"
          >
            <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: GOLD, boxShadow: `0 0 8px ${GOLD}` }} />
            The team behind this website
          </div>
          <h1
            className="font-cab"
            style={{
              fontSize: "clamp(2.75rem, 6vw, 5rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.015em",
              marginTop: 28,
              color: "#FFFFFF",
              fontWeight: 900,
              maxWidth: 820,
            }}
            data-testid="pbn-headline"
          >
            We build websites that
            <br />
            <span style={{ color: GOLD }}>quietly print revenue.</span>
          </h1>
          <p
            style={{
              marginTop: 26,
              fontSize: "1.2rem",
              lineHeight: 1.55,
              color: "rgba(245,245,240,0.82)",
              maxWidth: 640,
              fontFamily: "'Inter', system-ui, sans-serif",
            }}
          >
            NariAI is a small, senior team of designers, engineers, and SEO
            specialists. We take a local business, learn where its revenue
            leaks, and build a website that plugs the leak and grows month over
            month.
          </p>
          <div style={{ marginTop: 40, display: "inline-flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
            <a
            href="tel:+12148155966"
              data-testid="pbn-hero-phone"
              className="font-cab"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                padding: "16px 28px",
                borderRadius: 3,
                textDecoration: "none",
                background: `linear-gradient(180deg,${GOLD_HI} 0%,${GOLD} 50%,${GOLD_LO} 100%)`,
                color: "#0b0b0b",
                fontWeight: 800,
                letterSpacing: "0.03em",
                fontSize: "1.05rem",
                boxShadow: "0 8px 24px rgba(201,162,75,0.35), inset 0 1px 0 rgba(255,255,255,0.4)",
                transition: "transform 200ms ease, box-shadow 200ms ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 28px rgba(201,162,75,0.5), inset 0 1px 0 rgba(255,255,255,0.4)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(201,162,75,0.35), inset 0 1px 0 rgba(255,255,255,0.4)"; }}
            >
              Call Chris · 214-815-5966
            </a>
            <span
              style={{
                fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                fontSize: 11,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "rgba(245,245,240,0.5)",
              }}
            >
              No sales team.  No forms.  Just a conversation.
            </span>
          </div>
        </div>
      </section>

      {/* ------------------ 3-SERVICE GRID ---------------------------- */}
      <section
        ref={servRef}
        style={{ padding: "48px 24px 88px 24px", borderTop: "1px solid rgba(212,175,55,0.10)" }}
      >
        <div className="max-w-6xl mx-auto">
          <h2
            className="font-cab"
            style={{
              fontSize: "clamp(1.85rem, 3.4vw, 2.75rem)",
              lineHeight: 1.1,
              color: "#FFFFFF",
              fontWeight: 800,
              maxWidth: 760,
              letterSpacing: "-0.005em",
            }}
          >
            Three disciplines. One website that pulls its weight.
          </h2>
          <p style={{
            marginTop: 14,
            fontFamily: "'Inter', system-ui, sans-serif",
            fontSize: "1rem",
            color: "rgba(245,245,240,0.7)",
            maxWidth: 620,
            lineHeight: 1.6,
          }}>
            Design, automation, and SEO are usually three vendors and three
            invoices. We fold them into one team so nothing falls through the
            gaps.
          </p>
          <div
            className="grid gap-5 mt-14"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            }}
          >
            {services.map((s, i) => (
              <div
                key={s.title}
                data-testid={`pbn-service-${i + 1}`}
                className="pbn-service-card"
                style={{
                  background: "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0) 100%)",
                  border: "1px solid rgba(212,175,55,0.18)",
                  borderRadius: 4,
                  padding: "28px 26px 32px 26px",
                  position: "relative",
                  transform: servSeen ? "translateY(0)" : "translateY(16px)",
                  opacity: servSeen ? 1 : 0,
                  transition: `transform 700ms cubic-bezier(.2,.8,.2,1) ${140 + i * 100}ms, opacity 700ms ease ${140 + i * 100}ms, border-color 300ms ease, background 300ms ease`,
                }}
              >
                <div style={{ marginBottom: 16 }}>{s.icon}</div>
                <div
                  className="font-cab"
                  style={{ fontSize: "1.2rem", color: "#FFFFFF", fontWeight: 800, letterSpacing: "-0.005em" }}
                >
                  {s.title}
                </div>
                <div
                  style={{
                    marginTop: 6,
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "0.92rem",
                    lineHeight: 1.55,
                    color: "rgba(245,245,240,0.72)",
                  }}
                >
                  {s.lead}
                </div>
                <ul style={{ margin: "18px 0 0 0", padding: 0, listStyle: "none" }}>
                  {s.bullets.map((b) => (
                    <li key={b} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "6px 0", color: "rgba(245,245,240,0.82)", fontSize: "0.9rem", lineHeight: 1.5, fontFamily: "'Inter', system-ui, sans-serif" }}>
                      <IconCheck size={16} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------- CASE-STUDY STATS ------------------------ */}
      <section
        ref={statsRef}
        style={{ padding: "72px 24px", background: "linear-gradient(180deg, rgba(212,175,55,0.03) 0%, rgba(0,0,0,0) 100%)", borderTop: "1px solid rgba(212,175,55,0.12)", borderBottom: "1px solid rgba(212,175,55,0.12)" }}
      >
        <div className="max-w-6xl mx-auto">
          <div style={{ textAlign: "center", marginBottom: 30 }}>
            <div
              style={{
                fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                fontSize: 11,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: GOLD,
              }}
            >
              This site, by the numbers
            </div>
            <h3
              className="font-cab"
              style={{ marginTop: 12, fontSize: "clamp(1.65rem, 3vw, 2.35rem)", color: "#FFFFFF", fontWeight: 800, letterSpacing: "-0.005em" }}
            >
              A transparent look at what we engineered.
            </h3>
          </div>
          <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}>
            {stats.map((s, i) => (
              <div
                key={s.label}
                data-testid={`pbn-stat-${i + 1}`}
                style={{
                  textAlign: "center",
                  padding: "22px 14px",
                  background: "rgba(255,255,255,0.02)",
                  border: "1px solid rgba(212,175,55,0.14)",
                  borderRadius: 2,
                  transform: statsSeen ? "translateY(0)" : "translateY(10px)",
                  opacity: statsSeen ? 1 : 0,
                  transition: `transform 600ms cubic-bezier(.2,.8,.2,1) ${100 + i * 60}ms, opacity 600ms ease ${100 + i * 60}ms`,
                }}
              >
                <div
                  className="font-cab"
                  style={{ fontSize: "clamp(1.6rem, 3vw, 2.15rem)", color: GOLD, fontWeight: 900, letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums" }}
                >
                  {s.value}
                </div>
                <div style={{ marginTop: 8, fontFamily: "'Inter', system-ui, sans-serif", fontSize: "0.83rem", lineHeight: 1.4, color: "rgba(245,245,240,0.7)" }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------------- HOW WE WORK --------------------------- */}
      <section ref={howRef} style={{ padding: "88px 24px" }}>
        <div className="max-w-6xl mx-auto">
          <div
            style={{
              fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
              fontSize: 11,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: GOLD,
            }}
          >
            How we work
          </div>
          <h3
            className="font-cab"
            style={{ marginTop: 12, fontSize: "clamp(1.85rem, 3.4vw, 2.75rem)", color: "#FFFFFF", fontWeight: 800, letterSpacing: "-0.005em", maxWidth: 720 }}
          >
            Four steps.  Zero surprises.
          </h3>
          <div className="grid gap-5 mt-12" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
            {how.map((step, i) => (
              <div
                key={step.n}
                data-testid={`pbn-step-${step.n}`}
                style={{
                  padding: "26px 22px",
                  borderLeft: `2px solid ${GOLD}`,
                  background: "rgba(212,175,55,0.02)",
                  transform: howSeen ? "translateY(0)" : "translateY(12px)",
                  opacity: howSeen ? 1 : 0,
                  transition: `transform 700ms cubic-bezier(.2,.8,.2,1) ${100 + i * 100}ms, opacity 700ms ease ${100 + i * 100}ms`,
                }}
              >
                <div
                  className="font-cab"
                  style={{ fontSize: "2rem", color: GOLD, letterSpacing: "-0.02em", fontWeight: 900, lineHeight: 1 }}
                >
                  {step.n}
                </div>
                <div
                  className="font-cab"
                  style={{ marginTop: 10, fontSize: "1.15rem", color: "#FFFFFF", fontWeight: 800 }}
                >
                  {step.title}
                </div>
                <div
                  style={{
                    marginTop: 8,
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "0.9rem",
                    lineHeight: 1.5,
                    color: "rgba(245,245,240,0.72)",
                  }}
                >
                  {step.body}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------ CTA -------------------------------- */}
      <section
        ref={ctaRef}
        style={{
          padding: "88px 24px 100px 24px",
          textAlign: "center",
          borderTop: "1px solid rgba(212,175,55,0.14)",
          background: "linear-gradient(180deg, rgba(212,175,55,0.04) 0%, rgba(0,0,0,0) 100%)",
        }}
      >
        <div
          className="max-w-3xl mx-auto"
          style={{
            transform: ctaSeen ? "translateY(0)" : "translateY(16px)",
            opacity: ctaSeen ? 1 : 0,
            transition: "transform 800ms cubic-bezier(.2,.8,.2,1), opacity 800ms ease",
          }}
        >
          <h3
            className="font-cab"
            style={{ fontSize: "clamp(2rem, 4vw, 3rem)", color: "#FFFFFF", fontWeight: 900, letterSpacing: "-0.01em", lineHeight: 1.08 }}
          >
            Want a website that
            <br />
            <span style={{ color: GOLD }}>works this hard for you?</span>
          </h3>
          <p
            style={{
              marginTop: 20,
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: "1.05rem",
              color: "rgba(245,245,240,0.75)",
              lineHeight: 1.55,
            }}
          >
            Call Chris directly.  A 15-minute conversation is all it takes to
            see whether we&apos;re a fit.  No forms, no obligation, no upsell.
          </p>
          <a
            href="tel:+12148155966"
            data-testid="pbn-cta-phone"
            className="font-cab"
            style={{
              display: "inline-block",
              marginTop: 30,
              fontSize: "clamp(2.25rem, 5vw, 3.6rem)",
              fontWeight: 900,
              letterSpacing: "-0.01em",
              color: GOLD,
              textDecoration: "none",
              textShadow: "0 4px 22px rgba(212,175,55,0.32)",
              transition: "transform 220ms ease, color 220ms ease, text-shadow 220ms ease",
              fontVariantNumeric: "tabular-nums",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.color = GOLD_HI; e.currentTarget.style.textShadow = "0 6px 26px rgba(212,175,55,0.5)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.color = GOLD; e.currentTarget.style.textShadow = "0 4px 22px rgba(212,175,55,0.32)"; }}
          >
            214-815-5966
          </a>
          <div
            style={{
              marginTop: 26,
              fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
              fontSize: 11,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "rgba(245,245,240,0.42)",
            }}
          >
            Based in Dallas, Texas.  Serving local businesses across the U.S.
          </div>
        </div>
      </section>

      {/* ------------------------ FOOTER ---------------------------- */}
      <footer
        style={{
          padding: "40px 24px 60px 24px",
          borderTop: "1px solid rgba(212,175,55,0.10)",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontFamily: "'Cormorant Garamond', 'Cormorant', serif",
            fontStyle: "italic",
            fontSize: "0.95rem",
            color: "rgba(245,245,240,0.4)",
            letterSpacing: "0.02em",
          }}
        >
          NariAI — Design, engineering, and long-term growth.
        </div>
        <button
          type="button"
          onClick={() => nav(-1)}
          style={{
            marginTop: 22,
            background: "transparent",
            border: "1px solid rgba(212,175,55,0.35)",
            color: "rgba(245,245,240,0.72)",
            padding: "10px 20px",
            borderRadius: 999,
            fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            cursor: "pointer",
            transition: "background 200ms ease, color 200ms ease, border-color 200ms ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(212,175,55,0.08)"; e.currentTarget.style.color = GOLD_HI; e.currentTarget.style.borderColor = "rgba(212,175,55,0.6)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(245,245,240,0.72)"; e.currentTarget.style.borderColor = "rgba(212,175,55,0.35)"; }}
          data-testid="pbn-footer-back"
        >
          ← Back to the site you came from
        </button>
      </footer>

      <style>{`
        .pbn-service-card:hover {
          border-color: rgba(212,175,55,0.42) !important;
          background: linear-gradient(180deg, rgba(212,175,55,0.05) 0%, rgba(255,255,255,0) 100%) !important;
        }
      `}</style>
    </div>
  );
}
